<template>
  <focus-trap
    :escapeDeactivates="false"
    :clickDeactivates="false"
    :initialFocus="() => $refs['searchInput']"
  >
    <div
      class="reservation-patients-search-popup"
      @keyup.esc.stop="close"
      tabindex="-1"
    >
      <popup
        :title="'検索'"
        :shortFlg="true"
        @close="close"
        @click-close-mark="close"
        :buttons="['閉じる']"
      >
        <template v-slot:content>
          <div class="content">
            <div class="search-input">
              <input
                type="text"
                :placeholder="placeholder"
                v-model="inputText"
                data-e2e="patients-search-text-box"
                ref="searchInput"
              />
            </div>
            <div class="tab">
              <div class="tab-item-wrapper">
                <div class="tab-item" data-e2e="item-count">
                  患者<span>（{{ patientsListData.length }}件）</span>
                </div>
              </div>
            </div>
            <div class="list-wrapper">
              <div class="list-header">
                <div class="list-header-label">飼主ID</div>
                <div class="list-header-label">飼主名</div>
                <div class="list-header-label">患者ID</div>
                <div class="list-header-label">患者名</div>
              </div>
              <template>
                <dynamic-scroller
                  class="list-body"
                  :items="patientsListData"
                  :min-item-size="50"
                >
                  <template v-slot="{ item, index, active }">
                    <dynamic-scroller-item
                      :item="item"
                      :active="active"
                      :data-index="index"
                    >
                      <div
                        class="list-item"
                        :class="{ selected: item.id === dispPatientId }"
                        @click="clickPatient(item.id)"
                      >
                        <div
                          class="selected-check-mark"
                          v-if="item.id === dispPatientId"
                        ></div>
                        <div class="list-item-data owner-show-id">
                          {{ item.ownerShowId }}
                        </div>
                        <div class="list-item-data owner-name">
                          {{ item.ownerName }}
                        </div>
                        <div class="list-item-data patient-show-id">
                          {{ item.showId }}
                        </div>
                        <div class="list-item-data patient-name">
                          {{ item.name }}
                        </div>
                      </div>
                    </dynamic-scroller-item>
                  </template>
                </dynamic-scroller>
              </template>
            </div>
          </div>
        </template>
      </popup>
    </div>
  </focus-trap>
</template>

<script>
import Popup from '@/components/popups/Popup'
import { kataToHira } from '@/utils/convert_string'
import { compareShowId } from '@/utils/sort_show_id'
import { FocusTrap } from 'focus-trap-vue'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import { mapGetters } from 'vuex'

export default {
  name: 'ReservationPatientsSearchPopup',
  components: {
    Popup,
    FocusTrap,
    DynamicScroller,
    DynamicScrollerItem
  },
  props: {
    dispOwnerId: { type: Number },
    dispPatientId: { type: Number },
    initialSearchPatients: { type: Array }
  },
  data() {
    return {
      searchText: '',
      patients: [],
      owners: [],
      placeholder: '飼主ID、飼主名、患者ID、患者名',
      patientsListData: [],
      originalPatients: [],
      initialPatients: [],
      timeoutId: null
    }
  },
  computed: {
    ...mapGetters({
      patientsData: 'patients/getData',
      getOwner: 'owners/getDataById'
    }),
    inputText: {
      get() {
        return this.searchText
      },
      set(val) {
        clearTimeout(this.timeoutId)
        this.timeoutId = setTimeout(() => {
          this.searchText = val
        }, 300)
      }
    }
  },
  watch: {
    searchText: function() {
      this.search()
    }
  },
  created() {
    this.patients = this.patientsData.filter(v => v.deathFlg === 0)
    this.patientsListData = this.makePatientsListData()
  },
  beforeDestroy() {
    clearTimeout(this.timeoutId)
  },
  methods: {
    makePatientsListData() {
      return this.dispOwnerId === 0
        ? this.makeOriginalPatients()
        : this.makeInitialPatients()
    },
    makeOriginalPatients() {
      this.originalPatients = this.patients
        .map(patient => {
          const owner = this.getOwner(patient.ownerId)
          return {
            id: patient.id,
            ownerId: patient.ownerId,
            ownerShowId: owner.showId,
            ownerName: owner.lastName + ' ' + owner.firstName,
            patientId: patient.id,
            showId: patient.showId,
            name: patient.name,
            nameKana: patient.nameKana,
            ownerFullName: owner.lastName + owner.firstName,
            ownerFullNameKana: owner.lastNameKana + owner.firstNameKana
          }
        })
        .sort((a, b) => {
          return a.ownerShowId === b.ownerShowId
            ? compareShowId(a.showId, b.showId)
            : compareShowId(a.ownerShowId, b.ownerShowId)
        })
      return this.originalPatients
    },
    makeInitialPatients() {
      const owner = this.getOwner(this.dispOwnerId)
      this.initialPatients = this.initialSearchPatients
        .map(patient => {
          return {
            id: patient.id,
            ownerId: patient.ownerId,
            ownerShowId: owner.showId,
            ownerName: owner.lastName + ' ' + owner.firstName,
            patientId: patient.id,
            showId: patient.showId,
            name: patient.name,
            nameKana: patient.nameKana,
            ownerFullName: owner.lastName + owner.firstName,
            ownerFullNameKana: owner.lastNameKana + owner.firstNameKana
          }
        })
        .sort((a, b) => {
          return a.ownerShowId === b.ownerShowId
            ? compareShowId(a.showId, b.showId)
            : compareShowId(a.ownerShowId, b.ownerShowId)
        })
      return this.initialPatients
    },
    search() {
      const word = this.searchText.trim()
      const patients =
        this.dispOwnerId === 0 ? this.originalPatients : this.initialPatients
      this.patientsListData = patients.filter(patient => {
        return (
          (patient.showId + '').includes(word) ||
          patient.name.toLowerCase().includes(word.toLowerCase()) ||
          patient.nameKana.includes(word) ||
          kataToHira(patient.nameKana).includes(word) ||
          (patient.ownerShowId + '').includes(word) ||
          patient.ownerFullName.toLowerCase().includes(word.toLowerCase()) ||
          patient.ownerFullNameKana.includes(word) ||
          kataToHira(patient.ownerFullNameKana).includes(word)
        )
      })
    },
    close() {
      this.$emit('closeSearch')
    },
    clickPatient(patientId) {
      this.$emit('selectPatient', patientId)
    }
  }
}
</script>

<style lang="scss" scoped>
.reservation-patients-search-popup {
  .content {
    width: 700px;
    min-height: 470px;
    > .search-input {
      display: flex;
      flex-direction: row;
      margin: auto;
      margin-top: 10px;
      height: 60px;
      > input {
        margin: auto;
        width: 227px;
        height: 33px;
        font-size: 11px;
        padding-left: 15px;
        padding-right: 40px;
        box-sizing: border-box;
        border: solid 2px #{$light-grey};
        border-radius: 17px;
        -webkit-border-radius: 17px;
        -moz-border-radius: 17px;
        border-radius: 17px;
        -webkit-border-radius: 17px;
        -moz-border-radius: 17px;
        background-image: url('../../assets/images/search.png');
        background-size: 18px 18px;
        background-repeat: no-repeat;
        background-position: center right 12px;
      }
    }
    > .tab {
      margin: auto;
      flex-direction: row;
      display: flex;
      text-align: center;
      > .tab-item-wrapper {
        margin: auto;
        margin-top: 10px;
        > .tab-item {
          float: left;
          width: 150px;
          text-align: center;
          font-size: 15px;
          height: 30px;
          border-bottom: 4px solid #{$pumpkin};
          color: #{$pumpkin};
        }
      }
    }
    > .list-wrapper {
      width: 700px;
      height: 350px;
      overflow: hidden;
      color: #{$greyish-brown};
      font-size: 15px;
      > .list-header {
        width: 100%;
        height: 40px;
        background-color: #{$white_f8};
        border-top: 1px solid #{$light-grey};
        border-bottom: 1px solid #{$light-grey};
        padding-left: 50px;
        > .list-header-label {
          float: left;
          text-align: left;
          margin-top: 10px;
          width: 158px;
        }
      }
      .list-body {
        width: 100%;
        height: 310px;
        overflow-y: scroll;
        overflow-x: hidden;
        .list-item {
          width: 100%;
          height: 50px;
          display: flex;
          padding-left: 50px;
          @include hover();
          > .list-item-data {
            margin: auto 0 auto 0;
            text-align: left;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 158px;
          }
        }
        .selected {
          background-color: #{$white_f8};
          position: relative;
          > .selected-check-mark {
            position: absolute;
            top: 15px;
            left: 15px;
            width: 20px;
            height: 20px;
            background-image: url('../../assets/images/success.png');
            background-size: 20px 20px;
          }
        }
      }
    }
  }
}
</style>
